import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ReviewsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const reviewsData = {
    title: "Client Success Stories",
    rating: 4.9,
    totalReviews: 2318,
    reviews: [
      {
        id: 1,
        name: "Aisha Rahman",
        role: "I-130 Petition",
        rating: 5,
        comment:
          "The step-by-step Q&A replaced confusing fields. I paid and downloaded a clean, ready-to-file PDF, then handed off to an attorney in one click.",
        image: "AR",
        color: "from-blue-500 to-blue-600",
      },
      {
        id: 2,
        name: "Daniel Park",
        role: "I-485 Adjustment of Status",
        rating: 5,
        comment:
          "Clear guidance, smart checks, and a helpful document checklist. The system flagged missing details before I submitted. Great experience.",
        image: "DP",
        color: "from-purple-500 to-purple-600",
      },
      {
        id: 3,
        name: "Lucía Gómez",
        role: "Work Authorization (I-765)",
        rating: 5,
        comment:
          "I answered simple questions and the form was auto-filled perfectly. I appreciated the secure payment and instant PDF download.",
        image: "LG",
        color: "from-indigo-500 to-indigo-600",
      },
      {
        id: 4,
        name: "Omar Hassan",
        role: "Citizenship (N-400)",
        rating: 5,
        comment:
          "Straightforward, fast, and well organized. I also requested a law firm review and got connected with a licensed attorney quickly.",
        image: "OH",
        color: "from-pink-500 to-pink-600",
      },
      {
        id: 5,
        name: "Sofia Petrova",
        role: "Student Pathway (Prep)",
        rating: 5,
        comment:
          "Professional from start to finish. The guided flow made everything less stressful and the support team was super responsive.",
        image: "SP",
        color: "from-cyan-500 to-cyan-600",
      },
      {
        id: 6,
        name: "Priya Sharma",
        role: "Affidavit of Support (I-864)",
        rating: 5,
        comment:
          "Loved the plain-English explanations. I finalized payment, downloaded the PDF, and scheduled an attorney review for peace of mind.",
        image: "PS",
        color: "from-teal-500 to-teal-600",
      },
    ],
  };

  // Auto-rotate featured review
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviewsData.reviews.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [reviewsData.reviews.length]);

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <motion.svg
        key={i}
        className={`w-5 h-5 ${i < rating ? "text-yellow-400" : "text-gray-300"}`}
        fill="currentColor"
        viewBox="0 0 20 20"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: i * 0.1, type: "spring", stiffness: 200 }}
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </motion.svg>
    ));

  const currentReview = reviewsData.reviews[currentIndex];

  return (
    <section className="relative bg-white py-12 lg:py-16 overflow-hidden">
      {/* Animated Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-24 -right-24 w-96 h-96 bg-blue-100 rounded-full opacity-30 blur-3xl"
          animate={{
            x: [0, 50, 0],
            y: [0, 30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute -bottom-24 -left-24 w-96 h-96 bg-purple-100 rounded-full opacity-30 blur-3xl"
          animate={{
            x: [0, -50, 0],
            y: [0, -30, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.h2
            className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            {reviewsData.title}
          </motion.h2>

          <motion.div
            className="flex items-center justify-center gap-3 mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-1">{renderStars(5)}</div>
            <span className="text-3xl font-bold text-blue-600">
              {reviewsData.rating}
            </span>
            <span className="text-gray-500">out of 5</span>
          </motion.div>

          <motion.p
            className="text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Based on <strong>{reviewsData.totalReviews.toLocaleString()}</strong>{" "}
            verified reviews
          </motion.p>
        </motion.div>

        {/* Featured Review Carousel */}
        <div className="max-w-4xl mx-auto mb-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 100, rotateY: 90 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              exit={{ opacity: 0, x: -100, rotateY: -90 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-8 lg:p-12 shadow-2xl relative overflow-hidden">
                {/* Animated particles */}
                <motion.div
                  className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full opacity-10"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.1, 0.2, 0.1],
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                <motion.div
                  className="absolute bottom-0 left-0 w-40 h-40 bg-white rounded-full opacity-10"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.1, 0.15, 0.1],
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                />

                <div className="relative z-10">
                  {/* Quote Icon */}
                  <motion.svg
                    className="w-12 h-12 text-white/30 mb-4"
                    fill="currentColor"
                    viewBox="0 0 32 32"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 150 }}
                  >
                    <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14h-6v-1c0-1.7 1.3-3 3-3h1V8h-2zm14 0c-3.3 0-6 2.7-6 6v10h10V14h-6v-1c0-1.7 1.3-3 3-3h1V8h-2z" />
                  </motion.svg>

                  <motion.p
                    className="text-xl lg:text-2xl text-white font-medium leading-relaxed mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    "{currentReview.comment}"
                  </motion.p>

                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <motion.div
                      className="flex items-center gap-4"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-blue-600 font-bold text-lg">
                          {currentReview.image}
                        </span>
                      </div>
                      <div>
                        <h4 className="text-white font-bold text-lg">
                          {currentReview.name}
                        </h4>
                        <p className="text-blue-200">{currentReview.role}</p>
                      </div>
                    </motion.div>

                    <motion.div
                      className="flex gap-1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      {Array.from({ length: 5 }).map((_, i) => (
                        <svg
                          key={i}
                          className="w-5 h-5 text-yellow-300"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Carousel Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {reviewsData.reviews.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentIndex ? "bg-blue-600 w-8" : "bg-gray-300 w-2"
                }`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>
        </div>

        {/* Review Cards Grid with Stagger Animation */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviewsData.reviews.slice(0, 3).map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{
                y: -8,
                transition: { duration: 0.2 },
              }}
              className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-100 hover:border-blue-400 hover:shadow-2xl transition-all cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <svg
                      key={i}
                      className="w-4 h-4 text-yellow-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                >
                  ⭐
                </motion.div>
              </div>

              <p className="text-gray-700 text-sm leading-relaxed mb-4 min-h-[80px]">
                "{review.comment}"
              </p>

              <div className="flex items-center pt-4 border-t border-gray-100">
                <motion.div
                  className={`w-12 h-12 rounded-full bg-gradient-to-br ${review.color} flex items-center justify-center mr-3 shadow-md`}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <span className="text-white font-bold text-sm">
                    {review.image}
                  </span>
                </motion.div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">
                    {review.name}
                  </h4>
                  <p className="text-xs text-gray-500">{review.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
